/**
 * AssigneePicker - Unified picker for selecting task assignees
 *
 * Supports both platform users (team members) and organization contacts.
 * Two-section layout with "Team Members" and "Contacts" sections.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { User, Building2, Search, X } from 'lucide-react';
import Avatar from '@components/ui/Avatar';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { useContactsStore } from '@stores/contactsStore';
import type { OrganizationContact } from '@services/api/contacts';

export interface AssigneeOption {
  type: 'user' | 'contact';
  id: string;
  displayName: string;
  avatarUrl?: string | undefined;
  email?: string | undefined;
  company?: string | undefined;
}

interface AssigneePickerProps {
  organizationId: string;
  projectMembers: Array<{
    id: string;
    user: {
      id: string;
      displayName: string;
      avatarUrl?: string;
      email?: string;
    };
  }>;
  currentAssignee?: AssigneeOption | null;
  onSelect: (assignee: AssigneeOption | null) => void;
  disabled?: boolean;
  className?: string;
}

export function AssigneePicker({
  organizationId,
  projectMembers,
  currentAssignee,
  onSelect,
  disabled = false,
  className,
}: AssigneePickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    searchResults: contactSearchResults,
    isSearching: isSearchingContacts,
    searchContacts,
    clearSearchResults,
  } = useContactsStore();

  // Debounced search for contacts
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);

      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      if (value.trim().length >= 2) {
        searchTimeout.current = setTimeout(() => {
          void searchContacts(organizationId, value.trim());
        }, 300);
      } else {
        clearSearchResults();
      }
    },
    [organizationId, searchContacts, clearSearchResults]
  );

  // Filter team members by search query
  const filteredMembers = projectMembers.filter((member) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.user.displayName.toLowerCase().includes(query) ||
      member.user.email?.toLowerCase().includes(query)
    );
  });

  // Filter out contacts that are already team members (by email match)
  const memberEmails = new Set(
    projectMembers.map((m) => m.user.email?.toLowerCase()).filter(Boolean)
  );
  const filteredContacts = contactSearchResults.filter(
    (contact) => !contact.email || !memberEmails.has(contact.email.toLowerCase())
  );

  const handleSelectMember = useCallback(
    (member: (typeof projectMembers)[0]) => {
      onSelect({
        type: 'user',
        id: member.user.id,
        displayName: member.user.displayName,
        avatarUrl: member.user.avatarUrl,
        email: member.user.email,
      });
      setIsOpen(false);
      setSearchQuery('');
      clearSearchResults();
    },
    [onSelect, clearSearchResults]
  );

  const handleSelectContact = useCallback(
    (contact: OrganizationContact) => {
      onSelect({
        type: 'contact',
        id: contact.id,
        displayName: contact.name || contact.email || 'Unknown',
        email: contact.email,
        company: contact.company,
      });
      setIsOpen(false);
      setSearchQuery('');
      clearSearchResults();
    },
    [onSelect, clearSearchResults]
  );

  const handleUnassign = useCallback(() => {
    onSelect(null);
    setIsOpen(false);
    setSearchQuery('');
    clearSearchResults();
  }, [onSelect, clearSearchResults]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
        clearSearchResults();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSearchResults]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const showDropdown = isOpen;
  const hasSearchQuery = searchQuery.trim().length >= 2;
  const showMembers = filteredMembers.length > 0;
  const showContacts = hasSearchQuery && filteredContacts.length > 0;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2 rounded-lg px-3 py-1.5',
          'hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors',
          'text-sm',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <User className="h-4 w-4 text-neutral-400" />
        {currentAssignee ? (
          <>
            <Avatar
              size="xs"
              {...(currentAssignee.avatarUrl && { src: currentAssignee.avatarUrl })}
              name={currentAssignee.displayName}
            />
            <span className="text-neutral-900 dark:text-white">
              {currentAssignee.displayName}
            </span>
            {currentAssignee.type === 'contact' && (
              <Building2 className="h-3 w-3 text-neutral-400" />
            )}
          </>
        ) : (
          <span className="text-neutral-400">{t('task.unassigned')}</span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-lg border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
          {/* Search input */}
          <div className="border-b border-neutral-200 p-2 dark:border-neutral-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('task.searchMembersContacts')}
                className={cn(
                  'w-full rounded-md border border-neutral-300 py-1.5 pl-8 pr-8 text-sm',
                  'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    clearSearchResults();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {/* Unassigned option */}
            <button
              type="button"
              onClick={handleUnassign}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              <div className="h-5 w-5 rounded-full bg-neutral-200 dark:bg-neutral-600" />
              <span className="text-neutral-500">{t('task.unassigned')}</span>
            </button>

            {/* Team Members section */}
            {showMembers && (
              <>
                <div className="border-t border-neutral-200 px-3 py-1.5 dark:border-neutral-700">
                  <span className="text-xs font-medium uppercase text-neutral-500">
                    {t('task.teamMembers')}
                  </span>
                </div>
                {filteredMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelectMember(member)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                      currentAssignee?.type === 'user' &&
                        currentAssignee?.id === member.user.id &&
                        'bg-primary-50 dark:bg-primary-900/20'
                    )}
                  >
                    <Avatar
                      size="xs"
                      name={member.user.displayName}
                      {...(member.user.avatarUrl && { src: member.user.avatarUrl })}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-neutral-900 dark:text-white">
                        {member.user.displayName}
                      </div>
                      {member.user.email && (
                        <div className="truncate text-xs text-neutral-500">
                          {member.user.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Contacts section */}
            {hasSearchQuery && (
              <>
                <div className="border-t border-neutral-200 px-3 py-1.5 dark:border-neutral-700">
                  <span className="text-xs font-medium uppercase text-neutral-500">
                    {t('task.contacts')}
                  </span>
                </div>
                {isSearchingContacts ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-neutral-500">
                      {t('task.searchingContacts')}
                    </span>
                  </div>
                ) : showContacts ? (
                  filteredContacts.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleSelectContact(contact)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                        currentAssignee?.type === 'contact' &&
                          currentAssignee?.id === contact.id &&
                          'bg-primary-50 dark:bg-primary-900/20'
                      )}
                    >
                      <div className="relative">
                        <Avatar
                          size="xs"
                          name={contact.name || contact.email || '?'}
                        />
                        <Building2 className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-white p-0.5 text-neutral-500 dark:bg-neutral-800" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-neutral-900 dark:text-white">
                          {contact.name || contact.email || 'Unnamed'}
                        </div>
                        {contact.email && contact.name && (
                          <div className="truncate text-xs text-neutral-500">
                            {contact.email}
                          </div>
                        )}
                        {contact.company && (
                          <div className="truncate text-xs text-neutral-400">
                            {contact.company}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-neutral-500">
                    {t('task.noContactsFound')}
                  </div>
                )}
              </>
            )}

            {/* Empty state when no results at all */}
            {!showMembers && !showContacts && !isSearchingContacts && hasSearchQuery && (
              <div className="px-3 py-4 text-center text-sm text-neutral-500">
                {t('task.noMembersOrContacts')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssigneePicker;
